import { useEffect, useState } from 'react';
import { BaseChartComponentProps, BaseHistoricalUtr, ChartDataProps } from '../types';

/**
 * Custom hook for fetching chart data.
 * Handles the common pattern of loading data when calculation changes.
 *
 * @param getChartData - Async function to fetch chart data
 * @param props - Chart component props containing utrsData, variables, calculation, currencyConfig
 * @returns The fetched data or undefined if loading/invalid
 */
export const useChartData = <T extends BaseHistoricalUtr, D>(
  getChartData: (props: ChartDataProps<T>) => Promise<D>,
  props: Pick<BaseChartComponentProps<T>, 'utrsData' | 'variables' | 'calculation' | 'currencyConfig'>
): D | undefined => {
  const [data, setData] = useState<D | undefined>();
  const { utrsData, variables, calculation, currencyConfig } = props;
  // Extract primitive values to avoid reference changes triggering re-fetches
  const currencySymbol = currencyConfig?.currencySymbol;
  const currencyCode = currencyConfig?.currencyCode;
  const surveyUnitConfigMap = currencyConfig?.surveyUnitConfigMap;

  useEffect(() => {
    if (calculation === undefined || variables === undefined) {
      return;
    }
    // Reconstruct currencyConfig from tracked primitives to satisfy lint rules
    const stableCurrencyConfig =
      surveyUnitConfigMap ? { currencySymbol, currencyCode, surveyUnitConfigMap } : undefined;
    getChartData({ utrsData, variables, calculation, currencyConfig: stableCurrencyConfig }).then((result) => setData(result));
    return () => {
      setData(undefined);
    };
  }, [calculation, getChartData, utrsData, variables, currencySymbol, currencyCode, surveyUnitConfigMap]);

  return data;
};
